import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// MUI
import { styled } from "@mui/material/styles";
import { Card, CardContent, Grid, Typography, Stack, Box } from "@mui/material";
import {
  useAppDispatch,
  // useAppSelector,
} from "../../../redux/hooks/redux-hooks";
import { setShowProjectAction } from "../../../redux/actions/ShowProjectActions";
import {
  fetchParticularProjectSP,
  // setProjectSPWithInitialValues,
} from "../../../redux/actions/ProjectSPActions";
import { fetchParticularProjectAllComments } from "../../../redux/actions/ProjectCommentActions";
import { IFilterProjectModel } from "../../../models/ProjectResponseModel";
import { fetchProjectDocuments } from "../../../redux/actions/ProjectDocumentActions";
import { displayDateFormart } from "../../../utility/utils";
import { fetchProjectQuestions } from "../../../redux/actions/WinProbabilityFactorActions";
import { fetchProjectCustomerPOCs } from "../../../redux/actions/CustomerPOCActions";
import LongMsg from "../../LongMsg";

const BootstrapCardContent = styled(CardContent)(() => ({
  "&.MuiCardContent-root:last-child": {
    paddingBottom: "0.5em",
  },
}));

interface IBidCardProps {
  data: IFilterProjectModel;
  activeProjectId: number;
  setActiveProjectId: React.Dispatch<React.SetStateAction<number>>;
  scrollToTopOnBidSelection: () => void;
  handleDrawerToggle?: () => void;
  projects: IFilterProjectModel[];
}

interface IBidListSectionProps {
  isFiltered: boolean;
  resetFlags: () => void;
  projects: IFilterProjectModel[];
  scrollToTopOnBidSelection: () => void;
  handleDrawerToggle?: () => void;
}

function BidCard({
  data,
  activeProjectId,
  setActiveProjectId,
  scrollToTopOnBidSelection,
  handleDrawerToggle,
  projects,
}: IBidCardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const projectId = searchParams.get("id");
    const projectExists = projects.filter(
      (project) => project.projectId === Number(projectId)
    );
    if (projectId && projectExists.length !== 0) {
      const showProjectDetails = (id: number) => {
        setActiveProjectId(id);
        dispatch(setShowProjectAction(true));
        dispatch(fetchParticularProjectSP(id));
        dispatch(fetchParticularProjectAllComments(id));
        dispatch(fetchProjectDocuments(id));
        dispatch(fetchProjectQuestions(id));
        dispatch(fetchProjectCustomerPOCs(id));
        scrollToTopOnBidSelection();
      };

      showProjectDetails(+projectId);
    } else {
      searchParams.delete("id");
      setSearchParams(searchParams);
    }
  }, [searchParams, dispatch, scrollToTopOnBidSelection]);

  const handleProjectClick = (id: number) => {
    dispatch(setShowProjectAction(true));
    dispatch(fetchParticularProjectSP(id));
    dispatch(fetchParticularProjectAllComments(id));
    dispatch(fetchProjectDocuments(id));
    dispatch(fetchProjectQuestions(id));
    dispatch(fetchProjectCustomerPOCs(id));
    scrollToTopOnBidSelection();
    // To hide the drawer component for mobile view
    if (handleDrawerToggle) {
      handleDrawerToggle();
    }
  };

  function cardClickHandler() {
    const projectId = searchParams.get("id");
    if (projectId) {
      setSearchParams({ id: data.projectId.toString() });
    }
    setActiveProjectId(data.projectId);
    handleProjectClick(data.projectId);
  }

  const projectStatusColor =
    data.projectStatusKey === "rejected" || data.projectStatusKey === "loss"
      ? "error"
      : data.projectStatusKey === "approved" || data.projectStatusKey === "win"
      ? "success.main"
      : data.projectStatusKey === "submitted" ||
        data.projectStatusKey === "bidSubmitted" ||
        data.projectStatusKey === "pending for approval"
      ? "primary"
      : data.projectStatusKey === "drafted"
      ? "secondary"
      : data.projectStatusKey === "savedAsDraft"
      ? "warning.main"
      : "inherit";

  return (
    <Card
      sx={{ cursor: "pointer", margin: "0.5em 0 0 0" }}
      onClick={cardClickHandler}
    >
      <BootstrapCardContent
        sx={{ p: "0.5em" }}
        className={
          activeProjectId === data.projectId ? "project-tab-active" : ""
        }
      >
        <Stack direction="row" gap={1} mb={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Project Name:
          </Typography>
          <Typography variant="subtitle2">
            <LongMsg
              msg={data.projectName!}
              limit={25}
              isExploratory={data.bidExploratoryStatus}
              isDropped={data.isBidDropped}
              priority={data.priority}
            />
          </Typography>
        </Stack>
        <Grid container spacing={0}>
          <Grid item xs={12} sm={7}>
            <Stack direction="row" gap={1}>
              <Typography variant="body2">SBG:</Typography>
              <Typography variant="body2">{data.sbgName}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Stack
              direction="row"
              gap={1}
              sx={{ justifyContent: { xs: "start", md: "end" } }}
            >
              <Typography variant="body2">Created:</Typography>
              <Typography variant="body2">
                {displayDateFormart(data.createdDate)}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" gap={1}>
              <Typography variant="body2">SBU:</Typography>
              <Typography variant="body2">{data.sbuName}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            {data.submittedDate ? (
              <Stack
                direction="row"
                gap={1}
                sx={{ justifyContent: { xs: "start", md: "end" } }}
              >
                <Typography variant="body2">Submitted:</Typography>
                <Typography variant="body2">
                  {displayDateFormart(data.submittedDate)}
                </Typography>
              </Stack>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack direction="row" gap={1}>
              <Typography variant="body2">BU/LOB:</Typography>
              <Typography variant="body2">{data.buName}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="body2"
              sx={{ textAlign: { xs: "start", md: "end" } }}
              color={projectStatusColor}
            >
              {data.projectStatusLabel}
            </Typography>
          </Grid>
        </Grid>
      </BootstrapCardContent>
    </Card>
  );
}

const BidListSection = ({
  isFiltered,
  resetFlags,
  projects,
  scrollToTopOnBidSelection,
  handleDrawerToggle,
}: IBidListSectionProps) => {
  const [activeProjectId, setActiveProjectId] = useState<number>(0);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (projects.length) {
      if (isFiltered) {
        dispatch(setShowProjectAction(true));
        dispatch(fetchParticularProjectSP(projects[0]?.projectId));
        dispatch(fetchParticularProjectAllComments(projects[0]?.projectId));
        dispatch(fetchProjectDocuments(projects[0]?.projectId));
        dispatch(fetchProjectQuestions(projects[0]?.projectId));
        dispatch(fetchProjectCustomerPOCs(projects[0]?.projectId));
        setActiveProjectId(projects[0]?.projectId);
        scrollToTopOnBidSelection();
        resetFlags(); // To reset the searched and filtered flags
      }
    } else {
      dispatch(setShowProjectAction(false));
    }
  }, [projects, dispatch, isFiltered, resetFlags, scrollToTopOnBidSelection]);

  return (
    <Box>
      {projects.map((project) => (
        <BidCard
          key={project.projectId}
          data={project}
          activeProjectId={activeProjectId}
          setActiveProjectId={setActiveProjectId}
          scrollToTopOnBidSelection={scrollToTopOnBidSelection}
          handleDrawerToggle={handleDrawerToggle}
          projects={projects}
        />
      ))}
    </Box>
  );
};

export default React.memo(BidListSection);
